/**
 * input.ts — keyboard and mouse into a single InputCommand.
 *
 * The client owns the camera angles and the swing dial, and sends intent only:
 * no positions, no outcomes (AGENTS.md: server-authoritative). Pointer lock is required
 * for mouselook; click the canvas to engage.
 *
 * The dial is aimed by mouse movement and is STICKY: it only re-aims when the
 * mouse moves more than DIAL_DEADZONE_PX in a frame, so
 * small aiming corrections do not change what you are about to throw. It points
 * at the direction the swing comes FROM — mouse up means the blade comes down.
 *
 * ALT TOGGLES FREE LOOK: while it is on, the mouse moves the camera and nothing
 * else. It is strictly a camera control, and the strictness is the point — looking
 * around must not turn your body, must not re-aim the swing dial, and must not
 * raise or lower your guard. Every one of those would make looking a gameplay
 * action, and in a game where the direction a swing comes from is the whole read, a
 * camera that quietly re-aims your attack is a trap. So free look accumulates its
 * own offsets and leaves `yaw`, `pitch` and `dial` — the three things that go to the
 * server or decide a swing — untouched.
 *
 * Being a toggle rather than a hold makes it a MODE, and a mode the player cannot
 * turn in is one they can get stuck in, so it is cleared whenever the window loses
 * focus or pointer lock — and the caller is expected to say so on screen.
 */

import {
  ACTION,
  FEEL,
  clampDialAngle,
  wrapAngle,
  type ActionCode,
  type InputCommand,
} from '@dueler/shared';

export interface InputController {
  /**
   * Where the BODY is aimed. Yaw goes to the server as the requested facing; pitch
   * aims the block guard. Free look does not move either of them.
   */
  readonly yaw: number;
  readonly pitch: number;
  /**
   * Where the CAMERA points: the body angles plus the free-look offset. This is
   * what places the camera, and it is the only thing that offset affects.
   */
  readonly lookYaw: number;
  readonly lookPitch: number;
  /** Whether free look is currently held. */
  readonly freeLook: boolean;
  /** Swing dial, radians: 0 = screen-right, counter-clockwise. */
  readonly dial: number;
  readonly blocking: boolean;
  /** LMB held: the swing is winding up and will launch on release. */
  readonly charging: boolean;
  readonly pointerLocked: boolean;
  /** Builds the next command, incrementing the sequence number. */
  nextCommand(): InputCommand;
  /**
   * Advances time-based input state — currently just easing the camera back behind
   * the character once free look is released. Called once per frame.
   */
  update(dtSeconds: number): void;
  /**
   * Tells input a swing is in flight, so the body turns heavy.
   *
   * Pushed in rather than worked out here: whether you are mid-swing is combat
   * state, and the predictor already knows it exactly. Input recomputing it from
   * clicks and clip timings would be a second answer to the same question.
   */
  setAttacking(value: boolean): void;
  /**
   * Suppresses all gameplay input while the player is typing in chat.
   *
   * A flag here rather than the caller unbinding and rebinding: the character has
   * live state — keys held, a block up, a swing charging — and that state has to be
   * RELEASED on the way in, not just ignored. Otherwise pressing Y mid-stride
   * leaves you walking, or holding a block that no key is holding.
   *
   * Mouse look is suppressed too. Pointer lock stays engaged while typing (see
   * `ui/chat.ts` for why), so without this the mouse would still turn the body
   * and re-aim the dial while the player is looking at the keyboard.
   */
  setTyping(value: boolean): void;
  dispose(): void;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/**
 * Straight up on the dial: the overhead, coming down on the crown.
 *
 * Named rather than written twice, because two controls now mean it — the dial
 * starts here, and scrolling back returns to it — and a second literal PI/2
 * would be a second definition of what "overhead" is.
 */
export const OVERHEAD_DIAL = clampDialAngle(Math.PI / 2);

/**
 * The five angles a mouse movement can touch. Split by WHO OWNS THEM: `yaw`,
 * `pitch` and `dial` are gameplay — two go to the server and the third decides
 * what a swing is — while the offsets are the camera's alone.
 */
export interface LookAngles {
  /** Body facing, sent to the server as the requested yaw. */
  yaw: number;
  /** Guard pitch, sent to the server. */
  pitch: number;
  /** Swing dial, radians. What the game reads; glides towards `dialTarget`. */
  dial: number;
  /** Where the dial is being aimed, radians. */
  dialTarget: number;
  /**
   * Accumulated mouse push, screen space with +Y UP, in pixels.
   *
   * The dial is aimed by the direction of this, not by the direction of one
   * mousemove. Sensors report integer deltas, so a diagonal flick arrives as a
   * run of nearly axis-aligned events and taking the last one made corners
   * almost unreachable. Summed over a fraction of a second they make a diagonal.
   */
  pushX: number;
  pushY: number;
  /** Camera-only yaw offset from the body's facing. */
  yawOffset: number;
  /** Camera-only pitch offset from the body's pitch. */
  pitchOffset: number;
}

/**
 * Folds one mouse movement into the look angles. Pure, so the rule that matters
 * can actually be checked: when `freeLook` is set, `yaw`, `pitch` and `dial` come
 * out untouched and only the offsets move.
 *
 * `speedPxPerSec` is passed in rather than measured here because it depends on the
 * gap since the previous event, which is the caller's business.
 */
export function applyMouseMove(
  angles: LookAngles,
  move: {
    dx: number;
    dy: number;
    speedPxPerSec: number;
    freeLook: boolean;
    /**
     * Gain on the BODY's yaw and pitch, 1 normally. Scaled down mid-swing so the
     * requested yaw cannot run away from the turn cap.
     *
     * Deliberately not applied to the dial or to free look. Aiming a swing and
     * looking about are not turning; only the body is heavy.
     */
    turnScale?: number;
  },
): LookAngles {
  const { dx, dy, freeLook } = move;
  const turn = move.turnScale ?? 1;
  const pushX = angles.pushX + dx;
  const pushY = angles.pushY - dy;

  if (freeLook) {
    return {
      ...angles,
      yawOffset: wrapAngle(angles.yawOffset - dx * FEEL.MOUSE_SENSITIVITY),
      // Clamped against the BODY's pitch, so the total stays inside the limits
      // ordinary mouselook obeys and free look cannot see somewhere unreachable.
      pitchOffset: clamp(
        angles.pitchOffset - dy * FEEL.MOUSE_SENSITIVITY,
        FEEL.PITCH_MIN - angles.pitch,
        FEEL.PITCH_MAX - angles.pitch,
      ),
    };
  }

  return {
    ...angles,
    yaw: wrapAngle(angles.yaw - dx * FEEL.MOUSE_SENSITIVITY * turn),
    pitch: clamp(
      angles.pitch - dy * FEEL.MOUSE_SENSITIVITY * turn,
      FEEL.PITCH_MIN,
      FEEL.PITCH_MAX,
    ),
    // Screen axes: +X is right, and moving the mouse UP (negative dy) must point
    // the dial up, so the Y component is negated.
    pushX,
    pushY,
    // Re-aim only once there is a deliberate amount of push behind it, so small
    // corrections do not change what you are about to throw.
    dialTarget:
      Math.hypot(pushX, pushY) >= FEEL.DIAL_MIN_PUSH_PX
        ? clampDialAngle(Math.atan2(pushY, pushX))
        : angles.dialTarget,
  };
}

/**
 * Decays the accumulated push and glides the dial towards where it is aimed.
 *
 * Called once per input tick with real elapsed time, so the feel does not change
 * with frame rate — the whole reason the old threshold was measured per second
 * rather than per event.
 */
export function settleDial(angles: LookAngles, dtSeconds: number): LookAngles {
  const decay = Math.pow(0.5, (dtSeconds * 1000) / FEEL.DIAL_PUSH_HALFLIFE_MS);
  const step = FEEL.DIAL_SLEW_RATE * dtSeconds;
  const delta = wrapAngle(angles.dialTarget - angles.dial);
  return {
    ...angles,
    pushX: angles.pushX * decay,
    pushY: angles.pushY * decay,
    // Snap the last sliver rather than crawling at it forever.
    dial:
      Math.abs(delta) <= step ? angles.dialTarget : wrapAngle(angles.dial + Math.sign(delta) * step),
  };
}

/**
 * Points the dial at one named direction outright, with no glide behind it.
 *
 * For a control that NAMES a direction instead of gesturing at one — the wheel's
 * overhead. `dial` is set as well as `dialTarget` because the swing goes out on
 * the same command as the aim: glide it and the slash leaves on whatever the
 * dial was still pointing at, arriving at the overhead a tick after it mattered.
 *
 * The accumulated push goes with it. Left behind, the next small correction
 * lands on top of a stale flick and re-aims the dial straight back to wherever
 * the mouse had been heading.
 */
export function aimDialAt(angles: LookAngles, angle: number): LookAngles {
  const aimed = clampDialAngle(angle);
  return { ...angles, dial: aimed, dialTarget: aimed, pushX: 0, pushY: 0 };
}

/**
 * Eases the camera-only offsets back toward zero. Exponential, so it is frame-rate
 * independent and cannot overshoot; snapped below a fifth of a degree so the camera
 * does not idle forever a hair off centre.
 */
export function easeLookOffsets(angles: LookAngles, dtSeconds: number): LookAngles {
  const k = Math.exp(-(dtSeconds * 1000) / FEEL.FREE_LOOK_RETURN_MS);
  const yawOffset = angles.yawOffset * k;
  const pitchOffset = angles.pitchOffset * k;
  return {
    ...angles,
    yawOffset: Math.abs(yawOffset) < 3e-3 ? 0 : yawOffset,
    pitchOffset: Math.abs(pitchOffset) < 3e-3 ? 0 : pitchOffset,
  };
}

export function createInput(canvas: HTMLCanvasElement): InputController {
  const held = new Set<string>();
  let angles: LookAngles = {
    yaw: 0,
    pitch: 0,
    // Start pointing up: an overhead is the most legible opening swing.
    dial: OVERHEAD_DIAL,
    dialTarget: OVERHEAD_DIAL,
    pushX: 0,
    pushY: 0,
    yawOffset: 0,
    pitchOffset: 0,
  };
  let seq = 0;
  let locked = false;
  let blocking = false;
  /** Whether a swing is in flight, so the body turns heavy. Set by the caller. */
  let attacking = false;
  let charging = false;
  let lastMoveAt = 0;
  /** The next discrete action to send. Cleared once sent, so a click fires once. */
  let pendingAction: ActionCode = ACTION.None;
  let freeLook = false;
  /** Whether the player is typing in chat, so gameplay input is suppressed. */
  let typing = false;

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (typing) return;
    held.add(ev.code);
    if (ev.code === 'KeyQ') pendingAction = ACTION.Feint;
    // Keyboard fallbacks for the emotes: plenty of mice have no side buttons at
    // all, and a control nobody can reach is not a control.
    if (ev.code === 'KeyZ') pendingAction = ACTION.Flourish;
    if (ev.code === 'KeyB') pendingAction = ACTION.Bow;

    // Keyed off the CODE, not `ev.altKey`: the latter is true for every key pressed
    // while Alt happens to be down, so Alt+W would toggle the camera mode. Repeats
    // are ignored or holding the key would flap the toggle at the OS repeat rate.
    if ((ev.code === 'AltLeft' || ev.code === 'AltRight') && !ev.repeat) {
      freeLook = !freeLook;
      // Alt alone reaches for the menu bar on some platforms, which drops pointer
      // lock and with it mouselook.
      if (locked) ev.preventDefault();
    }
  };
  const onKeyUp = (ev: KeyboardEvent): void => {
    // No `typing` guard: a key released while typing must still leave `held`, or
    // it stays stuck once typing ends. Entering typing clears the set anyway, so
    // this is only ever removing something that is not there.
    held.delete(ev.code);
    // Nothing to do for Alt: it toggles on press and stays until pressed again.
  };
  // Losing focus mid-key would otherwise leave the player walking forever.
  const onBlur = (): void => {
    held.clear();
    blocking = false;
    // Losing focus mid-swing releases it rather than charging forever.
    charging = false;
    // Free look is a mode now, so leaving it set across a focus loss would leave the
    // player unable to turn with no obvious cause. Cheaper to drop it.
    freeLook = false;
  };

  const onMouseMove = (ev: MouseEvent): void => {
    if (!locked || typing) return;

    const now = performance.now();
    const dtSeconds = Math.max(1e-3, (now - lastMoveAt) / 1000);
    lastMoveAt = now;

    angles = applyMouseMove(angles, {
      dx: ev.movementX,
      dy: ev.movementY,
      speedPxPerSec: Math.hypot(ev.movementX, ev.movementY) / dtSeconds,
      freeLook,
      turnScale: attacking ? FEEL.TURN_SENS_ATTACKING : 1,
    });
  };

  const onMouseDown = (ev: MouseEvent): void => {
    if (!locked || typing) return;
    // The side buttons are Back and Forward to a browser. Without this, pressing
    // one leaves the page mid-duel.
    if (ev.button >= 3) ev.preventDefault();
    // Press starts the windup; the server holds it there until `c` goes false.
    if (ev.button === 0) {
      pendingAction = ACTION.Slash;
      charging = true;
    }
    if (ev.button === 2) blocking = true;
    // The extra side buttons, if the mouse has them. `button` numbering is the
    // DOM's: 3 is X1, 5 is the third extra button where one exists.
    if (ev.button === 3) pendingAction = ACTION.Flourish;
    if (ev.button === 5) pendingAction = ACTION.Bow;
  };
  const onMouseUp = (ev: MouseEvent): void => {
    // Unguarded for the same reason as `onKeyUp`: releasing must always be heard.
    if (ev.button === 0) charging = false;
    if (ev.button === 2) blocking = false;
  };
  const onWheel = (ev: WheelEvent): void => {
    if (!locked || typing) return;
    // Wheel deltas are negative when scrolling away.
    if (ev.deltaY < 0) {
      // Scroll forward is a stab: away from you is the point going in.
      pendingAction = ACTION.Stab;
    } else if (ev.deltaY > 0) {
      // Scroll back is the overhead. NOT a new attack — it is the slash the dial
      // already throws when it points straight up, aimed and released in one
      // gesture, so the wheel reads as the two attacks that need no direction
      // chosen: away for the thrust, back for the blow on the crown.
      angles = aimDialAt(angles, OVERHEAD_DIAL);
      pendingAction = ACTION.Slash;
    }
  };
  // Without this the browser's menu eats the block button.
  const onContextMenu = (ev: Event): void => ev.preventDefault();

  const onClick = (): void => {
    if (!locked) void canvas.requestPointerLock();
  };
  const onPointerLockChange = (): void => {
    locked = document.pointerLockElement === canvas;
    if (!locked) {
      held.clear();
      blocking = false;
      charging = false;
      freeLook = false;
    }
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('wheel', onWheel, { passive: true });
  window.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('click', onClick);
  document.addEventListener('pointerlockchange', onPointerLockChange);

  const axis = (positive: string[], negative: string[]): number =>
    (positive.some((k) => held.has(k)) ? 1 : 0) - (negative.some((k) => held.has(k)) ? 1 : 0);

  return {
    get yaw() {
      return angles.yaw;
    },
    get pitch() {
      return angles.pitch;
    },
    get lookYaw() {
      return wrapAngle(angles.yaw + angles.yawOffset);
    },
    get lookPitch() {
      return clamp(angles.pitch + angles.pitchOffset, FEEL.PITCH_MIN, FEEL.PITCH_MAX);
    },
    get freeLook() {
      return freeLook;
    },
    get dial() {
      return angles.dial;
    },
    get blocking() {
      return blocking;
    },
    get charging() {
      return charging;
    },
    get pointerLocked() {
      return locked;
    },
    nextCommand() {
      seq += 1;
      const action = pendingAction;
      pendingAction = ACTION.None;
      return {
        seq,
        f: axis(['KeyW', 'ArrowUp'], ['KeyS', 'ArrowDown']),
        r: axis(['KeyD', 'ArrowRight'], ['KeyA', 'ArrowLeft']),
        // The BODY's angles, never the camera's: free look must not reach the wire.
        yaw: angles.yaw,
        pitch: angles.pitch,
        a: action,
        ang: angles.dial,
        b: blocking,
        c: charging,
      };
    },
    setAttacking(value) {
      attacking = value;
    },
    setTyping(value) {
      if (value === typing) return;
      typing = value;
      if (!value) return;
      // Release, do not merely ignore. Anything left set here would persist for
      // as long as the player is typing, with no key or button holding it.
      held.clear();
      blocking = false;
      charging = false;
      pendingAction = ACTION.None;
      // A mode the player cannot see is a mode they get stuck in, and they cannot
      // press Alt to clear it while the keyboard belongs to chat.
      freeLook = false;
    },
    update(dtSeconds) {
      // The dial settles whatever the camera is doing: free look does not aim it,
      // but a push already in flight should still decay and arrive.
      angles = settleDial(angles, dtSeconds);
      // Held free look keeps the camera wherever it was left; only releasing it
      // brings the camera home.
      if (freeLook) return;
      angles = easeLookOffsets(angles, dtSeconds);
    },

    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('click', onClick);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    },
  };
}
